import { create } from 'zustand';
import { api, ApiListResponse } from '@/lib/api';
import { getFieldDisplayName } from '@/utils/get-field-display-name';

// Field types matching the API response
export interface FieldSchema {
    name: string;
    table: string;
    data_type: string;
    default_value: string | null;
    max_length: number | null;
    numeric_precision: number | null;
    numeric_scale: number | null;
    is_nullable: boolean;
    is_primary_key: boolean;
    is_unique: boolean;
    foreign_key_column: string | null;
    foreign_key_table: string | null;
    comment: string | null;
}

export interface FieldMeta {
    id: number;
    collection: string;
    field: string;
    special: string[] | null;
    interface: string | null;
    options: Record<string, unknown> | null;
    display: string | null;
    display_options: Record<string, unknown> | null;
    readonly: boolean;
    hidden: boolean;
    sort: number | null;
    width: string | null;
    translations: Array<{ language: string; translation: string }> | null;
    note: string | null;
    conditions: Record<string, unknown>[] | null;
    required: boolean;
    group: string | null;
    validation: Record<string, unknown> | null;
    validation_message: string | null;
}

export interface Field {
    collection: string;
    field: string;
    type: string;
    name: string;
    schema: FieldSchema | null;
    meta: FieldMeta | null;
}

interface FieldsState {
    // State
    fields: Field[];
    hydrated: boolean;
    loading: boolean;
    error: string | null;

    // Actions
    hydrate: (force?: boolean) => Promise<void>;
    dehydrate: () => void;

    // Selectors
    getFieldsForCollection: (collection: string) => Field[];
    getFieldsForCollectionSorted: (collection: string) => Field[];
    getField: (collection: string, fieldKey: string) => Field | null;
    getPrimaryKeyFieldForCollection: (collection: string) => Field | null;
    getFieldGroupChildren: (collection: string, fieldKey: string) => Field[];
}

export const useFieldsStore = create<FieldsState>()((set, get) => ({
    fields: [],
    hydrated: false,
    loading: false,
    error: null,

    /**
     * Hydrate the fields store by fetching all fields from the API
     * @param force - Force re-hydration even if already hydrated
     */
    hydrate: async (force = false) => {
        const { hydrated, loading } = get();

        // Don't re-hydrate if already done or in progress (unless forced)
        if (!force && (hydrated || loading)) {
            console.log('[FieldsStore] Already hydrated or loading, skipping');
            return;
        }

        console.log('[FieldsStore] Starting hydration...');
        set({ loading: true, error: null });

        try {
            const response = await api.get<ApiListResponse<Field>>('/fields');
            // The API returns { data: Field[] }
            const fieldsRaw = response.data || [];

            console.log('[FieldsStore] Loaded fields:', fieldsRaw.length);

            // Parse fields - compute display name from translations or format title
            const fields = fieldsRaw.map((field) => ({
                ...field,
                name: getFieldDisplayName(field),
            }));

            set({ fields, hydrated: true, loading: false });
        } catch (error) {
            console.error('Failed to hydrate fields store:', error);
            set({
                error: error instanceof Error ? error.message : 'Failed to load fields',
                loading: false
            });
        }
    },

    /**
     * Clear the fields store (on logout)
     */
    dehydrate: () => {
        set({ fields: [], hydrated: false, loading: false, error: null });
    },

    /**
     * Get all fields for a collection
     */
    getFieldsForCollection: (collection: string) => {
        const { fields } = get();
        return fields.filter((field) => field.collection === collection);
    },

    /**
     * Get fields for a collection, sorted by meta.sort
     * Also includes group field handling
     */
    getFieldsForCollectionSorted: (collection: string) => {
        const { fields } = get();

        // Get all fields for the collection
        const collectionFields = fields.filter((f) => f.collection === collection);

        // Sort by meta.sort (nulls last)
        const sorted = [...collectionFields].sort((a, b) => {
            const sortA = a.meta?.sort ?? 999999;
            const sortB = b.meta?.sort ?? 999999;
            return sortA - sortB;
        });

        // Organize fields with groups
        const nonGroupFields = sorted.filter((field) => !field.meta?.group);
        const result: Field[] = [];

        for (const field of nonGroupFields) {
            result.push(field);

            // Add group children after the group field
            if (field.meta?.special?.includes('group')) {
                const groupChildren = sorted.filter((f) => f.meta?.group === field.field);
                result.push(...groupChildren);
            }
        }

        return result;
    },

    /**
     * Get a specific field
     */
    getField: (collection: string, fieldKey: string) => {
        const { fields } = get();
        return fields.find(
            (field) => field.collection === collection && field.field === fieldKey
        ) || null;
    },

    /**
     * Get the primary key field for a collection
     */
    getPrimaryKeyFieldForCollection: (collection: string) => {
        const { fields } = get();
        return fields.find(
            (field) => field.collection === collection && field.schema?.is_primary_key === true
        ) || null;
    },

    /**
     * Get nested fields for a given group field
     */
    getFieldGroupChildren: (collection: string, fieldKey: string) => {
        const { fields } = get();
        return fields.filter(
            (field) => field.collection === collection && field.meta?.group === fieldKey
        );
    },
}));

// formatTitle is now imported from @/utils/get-field-display-name

/**
 * Hook to get fields for a specific collection
 * Automatically hydrates if needed
 */
export function useCollectionFields(collection: string | null) {
    const {
        fields,
        hydrated,
        loading,
        error,
        hydrate,
        getFieldsForCollectionSorted
    } = useFieldsStore();

    // Auto-hydrate on first use
    if (!hydrated && !loading && collection) {
        hydrate();
    }

    return {
        fields: collection ? getFieldsForCollectionSorted(collection) : [],
        allFields: fields,
        loading,
        error,
        hydrated,
    };
}
